import { QueryRender } from "@/components/helpers/QueryRender"
import { useTimeMachine } from "@/contexts/TimeMachineContext"
import { AttendanceEvent } from "@/entities/attendanceEvent/AttendanceEventType"
import { useAttendanceEvents } from "@/entities/attendanceEvent/useAttendanceEvent"
import { useAbsenceRequestsForYoung } from "@/entities/absenceRequest/useAbsenceRequest"
import { AbsenceRequestContent } from "@/pages/AbsenceRequest"
import { useAuth } from "@/hooks/useAuth"
import {
   eachDayOfInterval,
   endOfMonth,
   format,
   isSameDay,
   isWeekend,
   startOfMonth,
   subMonths,
} from "date-fns"
import { fr } from "date-fns/locale"
import { useMemo, useState } from "react"
import { PageDialog } from "@/components/dialog/PageDialog"
import { AttendanceEventContent } from "../../pages/AttendanceEvent"
import { AbsenceRequest } from "@/entities/absenceRequest/absenceRequest"
import { icon_absent, icon_external, icon_late, icon_present, icon_request } from "@/utils/svgIcons"

export default function CalendarWidget({ youngId }: { youngId: string }) {
   const { user } = useAuth()
   const { now } = useTimeMachine()
   const [monthOffset, setMonthOffset] = useState(0)
   const [selectedEvent, setSelectedEvent] = useState<AttendanceEvent | null>(null)
   const [selectedRequest, setSelectedRequest] = useState<AbsenceRequest | null>(null)
   const { data: events, ...query } = useAttendanceEvents({ young_id: youngId })
   const { data: absenceRequests } = useAbsenceRequestsForYoung({ youngId })

   const currentMonth = useMemo(() => subMonths(now, monthOffset), [now, monthOffset])

   const days = useMemo(() => {
      const start = startOfMonth(currentMonth)
      const end = endOfMonth(currentMonth)
      return eachDayOfInterval({ start, end })
   }, [currentMonth])

   const weeks = useMemo(() => {
      const result = []
      let currentWeek = []

      // Ajouter des jours vides au début pour aligner avec le début de la semaine
      const firstDay = days[0]
      const firstDayOfWeek = firstDay.getDay()
      // En France, la semaine commence le lundi (1) et non le dimanche (0)
      const emptyDays = firstDayOfWeek === 0 ? 5 : firstDayOfWeek - 1
      for (let i = 0; i < emptyDays; i++) {
         currentWeek.push(null)
      }

      // Ajouter tous les jours du mois
      for (const day of days) {
         if (isWeekend(day)) continue

         currentWeek.push(day)
         if (currentWeek.length === 5) {
            result.push(currentWeek)
            currentWeek = []
         }
      }

      // Ajouter des jours vides à la fin si nécessaire
      if (currentWeek.length > 0) {
         while (currentWeek.length < 5) {
            currentWeek.push(null)
         }
         result.push(currentWeek)
      }

      return result
   }, [days])

   const eventsByDay = useMemo(() => {
      if (!events) return new Map()

      const eventMap = new Map<string, AttendanceEvent[]>()

      events.forEach((event) => {
         const start = startOfMonth(currentMonth)
         const end = endOfMonth(currentMonth)
         if (!(event.from <= end && event.to >= start)) return

         const daysInRange = eachDayOfInterval({ start: event.from, end: event.to })
         daysInRange.forEach((day) => {
            const key = format(day, "yyyy-MM-dd")
            const existingEvents = eventMap.get(key) || []
            eventMap.set(
               key,
               [...existingEvents, event].sort((a, b) => a.from.getTime() - b.from.getTime()),
            )
         })
      })

      return eventMap
   }, [events, currentMonth])

   const requestsByDay = useMemo(() => {
      if (!absenceRequests) return new Map()

      const requestMap = new Map<string, AbsenceRequest[]>()

      absenceRequests.forEach((request) => {
         // // we don't want validated requests
         // if (request.status === "validated" || request.status === "validated_but_to_justify") return

         const start = startOfMonth(currentMonth)
         const end = endOfMonth(currentMonth)
         if (!(request.from <= end && request.to >= start)) return

         const daysInRange = eachDayOfInterval({ start: request.from, end: request.to })
         daysInRange.forEach((day) => {
            const key = format(day, "yyyy-MM-dd")
            const existingRequests = requestMap.get(key) || []
            requestMap.set(
               key,
               [...existingRequests, request].sort((a, b) => a.from.getTime() - b.from.getTime()),
            )
         })
      })

      return requestMap
   }, [absenceRequests, currentMonth])

   return (
      <>
         <QueryRender query={query} data={events} emptyArrayIsValidData>
            {() => (
               <div className=" ">
                  <div className="min-h-[700px]">
                     <div className="mb-8">
                        <div className="filter-bar justify-between">
                           <button
                              className="btn btn-circle btn-ghost"
                              onClick={() => setMonthOffset((prev) => prev + 1)}
                           >
                              <svg
                                 xmlns="http://www.w3.org/2000/svg"
                                 className="h-6 w-6"
                                 fill="none"
                                 viewBox="0 0 24 24"
                                 stroke="currentColor"
                              >
                                 <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 19l-7-7 7-7"
                                 />
                              </svg>
                           </button>
                           <div className="flex flex-col sm:flex-row items-center gap-1">
                              <div className="text-lg font-medium">
                                 {format(currentMonth, "MMMM yyyy", { locale: fr })}
                              </div>
                           </div>
                           <button
                              className="btn btn-circle btn-ghost"
                              onClick={() => setMonthOffset((prev) => prev - 1)}
                           >
                              <svg
                                 xmlns="http://www.w3.org/2000/svg"
                                 className="h-6 w-6"
                                 fill="none"
                                 viewBox="0 0 24 24"
                                 stroke="currentColor"
                              >
                                 <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                 />
                              </svg>
                           </button>
                        </div>

                        <div className="grid grid-cols-5 gap-1">
                           {/* En-têtes des jours de la semaine */}
                           {["Lun", "Mar", "Mer", "Jeu", "Ven"].map((day) => (
                              <div
                                 key={day}
                                 className="text-center font-medium text-sm text-gray-600 mb-1"
                              >
                                 {day}
                              </div>
                           ))}

                           {/* Jours du mois */}
                           {weeks.map((week, weekIndex) =>
                              week.map((date, dayIndex) => {
                                 if (!date) return <div key={`empty-${weekIndex}-${dayIndex}`} />
                                 // const isWeekDay = !isWeekend(date)

                                 // if (!isWeekDay) return null;

                                 return (
                                    <div
                                       key={date.toISOString()}
                                       className={`w-full  min-h-[100px]   relative rounded-lg 
                                       ${isSameDay(date, now) ? "bg-base-100 border border-neutral" : "bg-gray-200"} `}
                                       title={format(date, "EEEE d MMMM", { locale: fr })}
                                    >
                                       <div className="flex flex-col items-center h-full p-2">
                                          <div className="text-sm font-bold text-black">
                                             {format(date, "d", { locale: fr })}
                                          </div>
                                          <div className="flex flex-col gap-1 px-1 w-full mt-1">
                                             {eventsByDay
                                                .get(format(date, "yyyy-MM-dd"))
                                                ?.map((event: AttendanceEvent, i: number) => {
                                                   let color = "bg-white"
                                                   if (event.justified) {
                                                      color = "bg-white"
                                                   } else if (
                                                      event.justification_validation_status ===
                                                      "validated"
                                                   ) {
                                                      color = "bg-white"
                                                   } else if (
                                                      event.justification_validation_status ===
                                                      "admin_pending"
                                                   ) {
                                                      color = "bg-blue-300"
                                                   } else {
                                                      color = "bg-red-300"
                                                   }

                                                   let icon
                                                   if (event.type === "present") {
                                                      icon = icon_present
                                                      color = "bg-green-300"
                                                   } else if (event.type === "absent") {
                                                      icon = icon_absent
                                                   } else if (event.type === "late") {
                                                      icon = icon_late
                                                   } else if (event.type === "external") {
                                                      icon = icon_external
                                                   }

                                                   return (
                                                      <div
                                                         key={`${event.id}-${i}`}
                                                         className={
                                                            color +
                                                            " badge badge-lg  w-full cursor-pointer hover:brightness-90"
                                                         }
                                                         title={`${event.type}${event.justified ? " (justifié)" : ""}`}
                                                         onClick={(e) => {
                                                            e.stopPropagation()
                                                            setSelectedEvent(event)
                                                         }}
                                                      >
                                                         <span className=" ">{icon}</span>
                                                         {/* <span className="hidden sm:inline">
                                                      {event.type}
                                                   </span> */}
                                                      </div>
                                                   )
                                                })}

                                             {requestsByDay
                                                .get(format(date, "yyyy-MM-dd"))
                                                ?.map((request: AbsenceRequest, i: number) => {
                                                   let color = "bg-white"
                                                   if (request.status === "pending") {
                                                      color = "bg-blue-300"
                                                   } else if (request.status === "rejected") {
                                                      color = "bg-red-300"
                                                   } else if (request.status === "validated") {
                                                      color = "bg-green-300"
                                                   } else if (
                                                      request.status === "validated_but_to_justify"
                                                   ) {
                                                      color = "bg-green-300"
                                                   }

                                                   return (
                                                      <div
                                                         key={`request-${request.id}-${i}`}
                                                         className={
                                                            color +
                                                            " badge badge-lg  w-full cursor-pointer hover:brightness-90"
                                                         }
                                                         title="Demande d'absence en attente"
                                                         onClick={(e) => {
                                                            e.stopPropagation()
                                                            setSelectedRequest(request)
                                                         }}
                                                      >
                                                         {icon_request}
                                                      </div>
                                                   )
                                                })}
                                          </div>
                                       </div>
                                    </div>
                                 )
                              }),
                           )}
                        </div>
                     </div>

                     {/* <div className="flex gap-4 justify-center text-sm">
                     <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-500 rounded"></div>
                        <span>Présent</span>
                     </div>
                     <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                        <span>Retard</span>
                     </div>
                     <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-red-500 rounded"></div>
                        <span>Absent</span>
                     </div>
                     <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-gray-500 rounded"></div>
                        <span>Extérieur</span>
                     </div>
                  </div> */}
                  </div>
               </div>
            )}
         </QueryRender>

         <PageDialog isOpen={selectedEvent !== null} onClose={() => setSelectedEvent(null)}>
            {selectedEvent && (
               <AttendanceEventContent id={selectedEvent.id} user={user!} variant="full" onDeleted={() => setSelectedEvent(null)} />
            )}
         </PageDialog>

         <PageDialog isOpen={selectedRequest !== null} onClose={() => setSelectedRequest(null)}>
            {selectedRequest && <AbsenceRequestContent id={selectedRequest.id} variant="full" />}
         </PageDialog>
      </>
   )
}
